import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import KioskView from "@/components/KioskView";
import Login from "@/components/Login";
import AdminLayout from "@/components/AdminLayout";
import Dashboard from "@/components/Dashboard";
import Employees from "@/components/Employees";
import EmployeeProfile from "@/components/EmployeeProfile";
import Timesheets from "@/components/TimesheetsNew";
import FinanceTimesheets from "@/components/FinanceTimesheets";
import AuditLog from "@/components/AuditLog";
import EmployeePortal from "@/components/EmployeePortal";
import ManagerChoice from "@/components/ManagerChoice";
import CompanyCalendar from "@/components/CompanyCalendar";
import EmployeeCalendar from "@/components/EmployeeCalendar";
import Settings from "@/components/Settings";
import NotificationSettings from "@/components/NotificationSettings";
import EmailManagement from "@/components/EmailManagement";
import ManageTimeOff from "@/components/ManageTimeOff";
import Vehicles from "@/components/Vehicles";
import DriverSchedule from "@/components/DriverSchedule";
import DriverSchedulesView from "@/components/DriverSchedulesView";
import DriverLog from "@/components/DriverLog";
import SubmittedLogs from "@/components/SubmittedLogs";
import DriverReports from "@/components/DriverReports";
import SurveyBuilder from "@/components/SurveyBuilder";
import SurveyResults from "@/components/SurveyResults";
import EmployeeSurveys from "@/components/EmployeeSurveys";
import NewsletterBuilder from "@/components/NewsletterBuilder";
import AnnouncementBuilder from "@/components/AnnouncementBuilder";
import UpdateStats from "@/components/UpdateStats";
import ImpactPageBuilder from "@/components/ImpactPageBuilder";
import ImpactPageView from "@/components/ImpactPageView";
import TaskManager from "@/components/TaskManagerNew";
import EmployeeTasks from "@/components/EmployeeTasksNew";
import RoadmapPageBuilder from "@/components/RoadmapPageBuilder";
import RoadmapPageView from "@/components/RoadmapPageView";
import EmployeeDocuments from "@/components/EmployeeDocuments";
import SchedulesEnhanced from "@/components/SchedulesEnhanced";
import WarehouseTimesheets from "@/components/WarehouseTimesheets";
import WarehouseAnalytics from "@/components/WarehouseAnalytics";
import TOILDashboard from "@/components/TOILDashboard";
import TimesheetsManager from "@/components/TimesheetsManager";
import AllTimesheets from "@/components/AllTimesheets";
import DriversTimesheets from "@/components/DriversTimesheets";
import DriversTimesheetsManager from "@/components/DriversTimesheetsManager";
import YearEndReview from "@/components/YearEndReview";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [managerChoice, setManagerChoice] = useState(null); // 'account' or 'admin'

  // Check for version updates (auto-refresh on new deployment)
  useVersionCheck();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            setIsAuthenticated(true);
            setUser(data.user);
          } else {
            localStorage.removeItem('token');
          }
        })
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setManagerChoice(null);
  };

  const handleManagerChoice = (choice) => {
    setManagerChoice(choice);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-teal-600 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Root path - Login for managers/admins/employees */}
          <Route path="/" element={
            isAuthenticated ? (
              user?.role === 'admin' ? (
                <Navigate to="/admin/dashboard" />
              ) : user?.role === 'manager' ? (
                managerChoice === 'admin' ? <Navigate to="/admin/dashboard" /> :
                managerChoice === 'account' ? <Navigate to="/profile" /> :
                <ManagerChoice user={user} onChoice={handleManagerChoice} />
              ) : (
                <Navigate to="/profile" />
              )
            ) : (
              <Login onLogin={handleLogin} />
            )
          } />
          
          {/* Kiosk screen for employee clock in/out */}
          <Route path="/kiosk" element={<KioskView />} />
          
          {/* Payments terminal - redirect to standalone HTML */}
          <Route path="/payments" element={<Navigate to="/payments.html" replace />} />
          
          {/* Admin panel for managers and admins */}
          <Route path="/admin/*" element={
            isAuthenticated && (user?.role === 'admin' || user?.role === 'manager') ? (
              <AdminLayout user={user} onLogout={handleLogout} onSwitchView={() => setManagerChoice('account')}>
                <Routes>
                  <Route path="dashboard" element={<Dashboard user={user} />} />
                  <Route path="company-calendar" element={<CompanyCalendar user={user} />} />
                  <Route path="employees" element={<Employees user={user} />} />
                  <Route path="employees/:employeeId" element={<EmployeeProfile />} />
                  <Route path="schedules" element={<SchedulesEnhanced />} />
                  <Route path="warehouse/analytics" element={<WarehouseAnalytics />} />
                  <Route path="warehouse/timesheets" element={<WarehouseTimesheets />} />
                  <Route path="toil-overview" element={<TOILDashboard />} />
                  <Route path="warehouse-timesheets" element={<TimesheetsManager isFinance={true} />} />
                  <Route path="drivers-timesheets" element={<DriversTimesheetsManager isFinance={true} />} />
                  <Route path="operations/timesheets" element={<DriversTimesheets />} />
                  <Route path="all-timesheets" element={<AllTimesheets user={user} />} />
                  <Route path="finance/timesheets" element={<FinanceTimesheets user={user} />} />
                  <Route path="surveys" element={<SurveyBuilder />} />
                  <Route path="survey-results/:surveyId" element={<SurveyResults />} />
                  <Route path="newsletters" element={<NewsletterBuilder />} />
                  <Route path="announcements" element={<AnnouncementBuilder />} />
                  <Route path="tasks" element={<TaskManager />} />
                  <Route path="impact/stats" element={<UpdateStats />} />
                  <Route path="impact/page" element={<ImpactPageBuilder />} />
                  <Route path="roadmap" element={<RoadmapPageBuilder />} />
                  <Route path="audit-log" element={<AuditLog />} />
                  <Route path="settings/*" element={<Settings user={user} />} />
                  <Route path="notification-settings" element={<NotificationSettings portal="admin" />} />
                  <Route path="emails" element={<EmailManagement user={user} />} />
                  <Route path="vehicles" element={<Vehicles />} />
                  <Route path="driver-schedule" element={<DriverSchedule />} />
                  <Route path="submitted-logs" element={<SubmittedLogs />} />
                  <Route path="driver-reports" element={<DriverReports />} />
                  <Route path="year-end-review" element={<YearEndReview />} />
                </Routes>
              </AdminLayout>
            ) : <Navigate to="/" />
          } />
          
          {/* Employee portal for remote workers, managers, and admins */}
          <Route path="/profile" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null} />
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/manage-time-off" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <ManageTimeOff />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/employee/calendar" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <EmployeeCalendar user={user} />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/driver-schedules" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <DriverSchedulesView />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/driver-log" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <DriverLog />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/surveys" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <EmployeeSurveys />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/tasks" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <EmployeeTasks />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/impact-page" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <ImpactPageView />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/roadmap" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <RoadmapPageView />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/documents" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <EmployeeDocuments />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />

          <Route path="/profile/notification-settings" element={
            isAuthenticated && (user?.role === 'employee' || user?.role === 'manager' || user?.role === 'admin') ? (
              <EmployeePortal user={user} onLogout={handleLogout} onSwitchView={(user?.role === 'manager' || user?.role === 'admin') ? () => setManagerChoice('admin') : null}>
                <NotificationSettings portal="employee" />
              </EmployeePortal>
            ) : <Navigate to="/" />
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
